import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { navigate } from "gatsby";
import { useSelector } from "react-redux";
import { Deal } from "@ifgengineering/client-invest-sdk";
import { V3Text } from "@components/v3/primitives";
import {
  dealsSelector,
  neverLoadedDealSelector,
} from "@state/deal/deal.selectors";
import {
  dealUsesFundraisingRounds,
  fetchDealsFundraisingCapacityThunk,
} from "@state/dealCapacity";
import { useAppDispatch } from "@state/store";
import { commitmentsSelector } from "@state/commitment/commitment.selectors";
import useScreenView from "@hooks/useScreenView";
import { buildLiveDealCard, type LiveDealCard } from "./helpers";
import { useLiveDealsAnalytics } from "./useLiveDealsAnalytics";
import {
  groupLiveDealCards,
  LiveDealFundGroup,
  LiveDealListItem,
} from "./fundGroups";
import { DealCard } from "./components/DealCard";
import { FundCard } from "./components/FundCard";
import {
  CurrencyFilter,
  FilterBar,
  StatusFilter,
} from "./components/FilterBar";
import { useCopy } from "@hooks/useCopy";

const HIDDEN_STATUSES = new Set(["CLOSED", "OFFLINE"]);

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.section.gap}px;
`;

/* Two across: three at this card width crowds the stats row. */
const List = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.space.section.gap}px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.space["2xl"]}px 0;
`;

export interface LiveDealsV3Props {
  /** Defaults to every deal in the store; pass a list to render a subset. */
  deals?: Deal[];
  /** Drops the page chrome — the filters — when embedded in another page. */
  embedded?: boolean;
  onSelectDeal?: (deal: Deal) => void;
  onSelectFund?: (investableId: string) => void;
}

export const LiveDealsV3 = ({
  deals,
  embedded = false,
  onSelectDeal,
  onSelectFund,
}: LiveDealsV3Props): JSX.Element => {
  const copy = useCopy("home");
  const dispatch = useAppDispatch();
  const storeDeals = useSelector(dealsSelector);
  const neverLoadedDeals = useSelector(neverLoadedDealSelector);
  const source = deals ?? storeDeals;

  const [currency, setCurrency] = useState<CurrencyFilter>("All");
  const [status, setStatus] = useState<StatusFilter>("All");

  // Reuses the shipped live-deals view name so the funnel stays continuous.
  // Embedded copies sit inside a page that reports its own view.
  useScreenView("cur8:liveDeals_pageViewed", { ready: !embedded });
  const { trackFilterClicked, trackDealCardClicked, trackFundCardClicked } =
    useLiveDealsAnalytics();
  const commitments = useSelector(commitmentsSelector);
  const heldDealIds = useMemo(
    () => new Set(commitments.map((commitment) => String(commitment.dealId))),
    [commitments]
  );

  const allCards = useMemo(
    () =>
      source
        .filter((deal) => !HIDDEN_STATUSES.has(deal.status))
        .map((deal) => buildLiveDealCard(deal, copy)),
    [copy, source]
  );

  const showCurrencyFilter = useMemo(
    () => new Set(allCards.map((card) => card.currency)).size > 1,
    [allCards]
  );

  const showStatusFilter = useMemo(() => {
    const statuses = new Set(allCards.map((card) => card.deal.status));
    return statuses.has("PRELAUNCH") && statuses.has("LIVE_ALL");
  }, [allCards]);
  const activeStatus = showStatusFilter ? status : "All";

  const filtered = useMemo(
    () =>
      allCards.filter(
        (card) =>
          (currency === "All" || card.currency === currency) &&
          (activeStatus === "All" || card.deal.status === activeStatus)
      ),
    [allCards, currency, activeStatus]
  );

  const cappedDealIdsKey = useMemo(
    () =>
      allCards
        .filter((card) => dealUsesFundraisingRounds(card.deal))
        .map((card) => card.deal.id)
        .sort()
        .join(","),
    [allCards]
  );

  useEffect(() => {
    if (cappedDealIdsKey) {
      dispatch(fetchDealsFundraisingCapacityThunk(cappedDealIdsKey.split(",")));
    }
  }, [cappedDealIdsKey, dispatch]);

  const items = useMemo(() => groupLiveDealCards(filtered), [filtered]);

  const selectDeal = (card: LiveDealCard) => {
    trackDealCardClicked(card, heldDealIds.has(card.id));
    if (onSelectDeal) {
      onSelectDeal(card.deal);
      return;
    }
    navigate(`/app/deals/${card.deal.id}?referrer=Live Deals`);
  };

  const selectFund = (group: LiveDealFundGroup) => {
    trackFundCardClicked(group);
    if (onSelectFund) {
      onSelectFund(group.investableId);
      return;
    }
    navigate(`/app/funds/${group.investableId}`);
  };

  const renderItem = (item: LiveDealListItem) =>
    item.kind === "fund" ? (
      <FundCard key={item.key} group={item} onSelect={selectFund} />
    ) : (
      <DealCard key={item.card.id} card={item.card} onSelect={selectDeal} />
    );

  return (
    <Page data-testid="live-deals-v3">
      {!embedded && (showCurrencyFilter || showStatusFilter) && (
        <FilterBar
          currency={currency}
          onCurrencyChange={(value) => {
            trackFilterClicked("currency", value);
            setCurrency(value);
          }}
          status={status}
          onStatusChange={(value) => {
            trackFilterClicked("status", value);
            setStatus(value);
          }}
          showCurrencyFilter={showCurrencyFilter}
          showStatusFilter={showStatusFilter}
        />
      )}

      {filtered.length === 0 ? (
        <Empty>
          <V3Text variant="body.md" color="secondary">
            {neverLoadedDeals
              ? copy.liveDeals.loading
              : copy.liveDeals.emptyFilteredLong}
          </V3Text>
        </Empty>
      ) : (
        <List>{items.map(renderItem)}</List>
      )}
    </Page>
  );
};
